import { useEffect, useRef, useState } from 'react';
import {
  apiDocumentos,
  type AnaliseSocietaria,
  type DocumentoDoCaso,
  type EspecieDeDocumento,
  type SocioDoContrato,
} from '../lib/api.ts';
import { usarEhMaster } from '../lib/papel.ts';
import { avisar, avisarErro } from './Avisos.tsx';
import { Confirmar } from './Confirmar.tsx';
import { Icone } from './Icone.tsx';
import { CampoDoPasso } from './PassoAPasso.tsx';
import { SumarioDoRelatorio, type CampoDoRetorno } from './SumarioDoRelatorio.tsx';
import { apiRelatorios } from '../lib/api.ts';
import '../estilos/linha-rapida-2026.css';

/**
 * Os contratos sociais do caso, e a leitura deles.
 *
 * > *"Adicione um botão 'analisar contrato social', onde irei realizar o upload
 * > de todos os contratos sociais e você vai extrair as informações
 * > cronologicamente."* — Richard, 20/08/2026
 *
 * ⚠️ **Vive DENTRO do campo "Estrutura societária"**, e não numa aba própria:
 * é ali que o resultado é escrito, e botão longe do campo que ele preenche faz
 * quem usa procurar onde a informação foi parar.
 *
 * ## As três coisas que esta tela precisa deixar à vista
 *
 * 1. ⚠️ **A leitura é local e erra.** Contrato social não tem forma padrão —
 *    muda por junta, por década e por escritório. O que ela não achou sai como
 *    lacuna escrita, e não como silêncio.
 * 2. ⚠️ **O que ela chama de sinal NÃO é conclusão, e não vai ao relatório.**
 *    A palavra "laranja" não aparece aqui, como não aparece em lugar nenhum do
 *    Hub: é acusação, e quem acusa num documento assinado precisa de OAB atrás.
 * 3. ⚠️ **Reconhecer por NOME é mais fraco que por documento.** Homônimo é
 *    regra num país de duzentos milhões, e a tela diz qual dos dois foi usado.
 */
export function ContratosSociais({
  casoId,
  relatorioId,
  secaoId,
  travado,
  /** ⭐ 23/09/2026 — o passo "O texto que voltou" vira campo de escrita. */
  campo,
  aoGravarSecao,
}: {
  casoId: string;
  campo?: CampoDoRetorno;
  /** ⭐ 09/09/2026 — o resumo de um documento vira campo livre DESTA seção. */
  relatorioId: string;
  secaoId: string;
  /** Relatório entregue não se reescreve — nem por aqui. */
  travado: boolean;
  aoGravarSecao: () => Promise<void>;
}) {
  const [documentos, setDocumentos] = useState<DocumentoDoCaso[] | null>(null);
  const [analise, setAnalise] = useState<AnaliseSocietaria | null>(null);
  const [especie, setEspecie] = useState<EspecieDeDocumento>('contrato_social');
  const [ocupado, setOcupado] = useState<'enviando' | 'analisando' | null>(null);
  const [aApagar, setAApagar] = useState<DocumentoDoCaso | null>(null);
  const [aResumir, setAResumir] = useState<DocumentoDoCaso | null>(null);

  /*
   * ⭐⭐ **A RELEITURA — 10/09/2026.**
   *
   * ⚠️ **Dispara e acompanha por pergunta repetida.** O servidor responde na
   * hora e trabalha depois (~2 s por página); a tela pergunta de dez em dez
   * segundos como está. Sem isso o Richard ficaria olhando "começando…" para
   * sempre.
   *
   * ⚠️ **A pergunta só existe enquanto há algo sendo relido**, e para sozinha
   * quando acaba — tela que consulta o servidor para sempre é bateria e banda
   * gastas em silêncio.
   */
  async function reler(d: DocumentoDoCaso) {
    try {
      await apiDocumentos.reler(d.id);
      avisar(`Reconhecendo "${d.nome}". Pode continuar trabalhando.`);
      await carregar();
    } catch (e) {
      avisarErro(e);
    }
  }
  const escolher = useRef<HTMLInputElement>(null);
  const ehMaster = usarEhMaster();

  /*
   * ⚠️ **Pergunta de dez em dez segundos, e SÓ enquanto há releitura.** O
   * intervalo se desmonta sozinho quando o último documento termina — consulta
   * que continua para sempre é banda gasta em silêncio.
   */
  const relendoAlgum = (documentos ?? []).some((d) => d.relendo);
  useEffect(() => {
    if (!relendoAlgum) return;
    const t = setInterval(() => void carregar(), 10_000);
    return () => clearInterval(t);
  }, [relendoAlgum, casoId]);

  async function carregar() {
    try {
      setDocumentos((await apiDocumentos.listar(casoId)).documentos);
    } catch {
      setDocumentos([]);
    }
  }

  useEffect(() => {
    void carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [casoId]);

  /**
   * ⚠️ **Sobe um por vez, em fila, e não todos de uma vez.** O Richard disse
   * que vai subir "todos os contratos sociais": um relatório de junta passa de
   * 10 MB, e cinco em paralelo enchem a memória do servidor. Em fila, cada um
   * também informa o próprio erro — em paralelo, um erro qualquer derrubaria o
   * lote inteiro sem dizer qual arquivo falhou.
   */
  async function enviar(lista: FileList) {
    setOcupado('enviando');
    let subiram = 0;
    for (const arquivo of Array.from(lista)) {
      try {
        await apiDocumentos.subir(casoId, arquivo, especie);
        subiram += 1;
      } catch (e) {
        avisarErro(e);
      }
    }
    setOcupado(null);
    if (subiram > 0) {
      avisar(`${subiram} documento(s) enviado(s).`);
      await carregar();
    }
  }

  async function analisar(gravar: boolean) {
    setOcupado('analisando');
    try {
      const r = await apiDocumentos.analisar(casoId, gravar);
      setAnalise(r);
      if (r.gravado) {
        avisar('Seção preenchida com o levantamento. Revise antes de entregar.');
        await aoGravarSecao();
      } else if (r.jaTemTexto) {
        avisar(
          'A seção já tem texto, e o Hub não substitui o que está escrito. Copie do quadro acima o que ' +
            'quiser levar, ou apague o texto da seção e clique de novo.',
        );
      }
    } catch (e) {
      avisarErro(e);
    } finally {
      setOcupado(null);
    }
  }

  const legiveis = (documentos ?? []).filter((d) => d.legivel).length;

  return (
    <div className="contratos-sociais">
      <div className="contratos-topo">
        <h3 className="lr-titulo">Contratos sociais e relatórios de junta</h3>
        {!travado && (
          <div className="contratos-acoes lr-com-campo">
            <CampoDoPasso rotulo="O que é o arquivo" col={2}>
              <select
                value={especie}
                onChange={(e) => setEspecie(e.target.value as EspecieDeDocumento)}
                aria-label="O que é o arquivo"
                disabled={ocupado !== null}
              >
                <option value="contrato_social">Contrato social ou alteração</option>
                <option value="junta_comercial">Relatório completo da junta</option>
                <option value="outro">Outro documento</option>
              </select>
            </CampoDoPasso>
            <input
              ref={escolher}
              type="file"
              accept="application/pdf"
              multiple
              hidden
              onChange={(e) => {
                if (e.target.files?.length) void enviar(e.target.files);
                e.target.value = '';
              }}
            />
            <button
              type="button"
              className="imv-bt"
              onClick={() => escolher.current?.click()}
              disabled={ocupado !== null}
            >
              <Icone nome="subir" tamanho={14} /> {ocupado === 'enviando' ? 'Enviando…' : 'Enviar PDF'}
            </button>
            <button
              type="button"
              className="imv-bt primario"
              onClick={() => void analisar(false)}
              disabled={ocupado !== null || legiveis === 0}
              title={
                legiveis === 0
                  ? 'Envie ao menos um PDF com texto antes de analisar.'
                  : 'Lê os documentos e monta a linha do tempo societária'
              }
            >
              {ocupado === 'analisando' ? 'Analisando…' : 'Analisar contrato social'}
            </button>
          </div>
        )}
      </div>

      <p className="ajuda ajuda-solta">
        {/*
          ⛔ **Esta ajuda dizia duas coisas que deixaram de ser verdade.**

          *"O PDF precisa ter texto"* e *"a leitura automática não alcança esse
          formato"* valiam até 08/09/2026, quando o reconhecimento de imagem
          entrou. E *"o Hub avisa quando é o caso"* era promessa que o código
          não cumpria — corrigido em 09/09, com a marca na linha do arquivo.

          ⚠️ **Texto de tela que promete o que o sistema não faz é pior que tela
          sem texto: quem lê confia.**
        */}
        Somente PDF. ⚠️ <strong>Escaneado também é lido</strong>, por
        reconhecimento de imagem — e o arquivo lido assim fica marcado na lista,
        porque nome, data e valor saem sem rede de conferência. A leitura é
        feita <strong>aqui dentro</strong>: nenhum documento sai da KAIRÓS.
      </p>

      {documentos && documentos.length > 0 && (
        <ul className="lista-de-documentos">
          {documentos.map((d) => (
            <li key={d.id}>
              <a href={`/api/documentos/${d.id}/arquivo`} target="_blank" rel="noreferrer">
                {d.nome}
              </a>{' '}
              <span className="ajuda">
                {(d.tamanho / 1024 / 1024).toFixed(1)} MB ·{' '}
                {d.especie === 'junta_comercial' ? 'junta comercial' : d.especie === 'outro' ? 'outro' : 'contrato social'}
              </span>
              {/*
                ⭐ **O aviso de reconhecimento óptico — 09/09/2026.**

                ⛔ **A ajuda desta seção prometia "o Hub avisa quando é o caso" e
                o Hub não avisava.** A origem do texto era calculada ao subir,
                mostrada uma vez e perdida. No caso real entrou um escaneado, o
                reconhecimento devolveu texto parcialmente legível, e a linha do
                tempo ganhou um ato datado de **50/06/2020** sem ninguém saber de
                onde vinha.

                ⚠️ **Na linha do arquivo, como a falha** — é preciso saber QUAL
                arquivo foi lido assim.
              */}
              {/*
                ⭐⭐ **O TEXTO VEIO DO APARELHO QUE DIGITALIZOU — 10/09/2026.**

                > *"Reler as páginas aqui dentro"* — o botão que ele aprovou
                > depois de ver a cadeia societária sair com
                > *"solidariamethOpelaﬁntegralizagão"*.

                ⛔ **Este é o caso que faltava, e era o pior:** o PDF TEM texto,
                então o Hub o tratava como texto digital — mas quem escreveu
                aquilo foi o scanner, e escreveu mal. A sonda geométrica
                reconhece a folha fotografada (1,4% × 100% de cobertura) e o
                arquivo passa a dizer isso aqui.

                ⚠️ **O botão some enquanto a releitura corre**, e o progresso
                toma o lugar dele — botão que continua clicável durante o
                trabalho é como se pede a mesma coisa três vezes.
              */}
              {d.origemDoTexto === 'texto-de-scanner' && !d.relendo && (
                <p className="ajuda-alerta ajuda-solta">
                  ⚠️ O texto deste PDF foi escrito pelo <strong>aparelho que digitalizou</strong>,
                  não pelo sistema que gerou o documento — e vem com letras trocadas.{' '}
                  {ehMaster && !travado && (
                    <button type="button" className="botao-texto" onClick={() => void reler(d)}>
                      reler as páginas aqui dentro
                    </button>
                  )}
                </p>
              )}

              {d.relendo && (
                <p className="ajuda ajuda-solta">
                  ⏳ Reconhecendo as imagens deste documento — <strong>{d.releituraEstado ?? 'começando…'}</strong>.
                  Pode sair desta tela: o trabalho continua.
                </p>
              )}

              {!d.relendo && d.releituraEstado && d.origemDoTexto !== 'texto-de-scanner' && (
                <p className="ajuda ajuda-solta">✓ {d.releituraEstado}</p>
              )}

              {d.origemDoTexto === 'imagem' && (
                <p className="ajuda-alerta ajuda-solta">
                  ⚠️ Lido por <strong>reconhecimento de imagem</strong>. CPF e CNPJ têm o
                  dígito verificador como rede; <strong>nome, data e valor não têm</strong> —
                  confira contra o arquivo antes de usar.
                </p>
              )}
              {/* ⚠️ A falha aparece na linha do arquivo, e não num aviso solto:
                  é preciso saber QUAL arquivo não foi lido. */}
              {d.falha && <p className="ajuda-alerta ajuda-solta">{d.falha}</p>}
              {/*
                ⭐⭐ **RESUMIR COM IA — 09/09/2026, e SÓ para prosa.**

                > *"Consegue fazer de forma automática e autônoma, ou acha que
                > aqui seria interessante fazermos o prompt também?"* — Richard

                ⭐ **Cada documento com a sua régua.** A ficha cadastral da junta
                é formulário de máquina e tem leitor determinístico: régua não
                erra dígito e não leva o documento para fora. O contrato social
                é prosa de cartório, que muda por escritório e por década — ali
                a régua não ganha, e é aqui que o prompt vale.

                ⚠️ **`junta_comercial` não recebe o botão**, e é a expressão
                dessa decisão na tela: oferecer IA para o que a régua já lê
                melhor seria convidar a gastar exposição de dado à toa.
              */}
              {ehMaster && !travado && d.legivel && d.especie !== 'junta_comercial' && (
                <button type="button" className="botao-texto" onClick={() => setAResumir(d)}>
                  resumir com IA
                </button>
              )}
              {ehMaster && !travado && (
                <button type="button" className="botao-texto" onClick={() => setAApagar(d)}>
                  apagar
                </button>
              )}

              {aResumir?.id === d.id && (
                <div className="bloco-recuado">
                  <SumarioDoRelatorio
                    travado={travado}
                    /* ⭐ PRD §58.12, 19/10/2026: o "Gerar texto" também aqui, com o documento. */
                    redacao="resumo-de-documento"
                    documentoId={d.id}
                    /* ⚠️ **O título continua vindo do ARQUIVO** — decisão de
                       09/09/2026. É o único passo da casa cujo campo nasce com
                       nome, porque sem ele o leitor não sabe de qual documento
                       o resumo saiu. */
                    campo={campo && { ...campo, titulo: `Resumo — ${d.nome.replace(/\.pdf$/i, '')}` }}
                    fonte={{
                      apoio:
                        'O Hub monta o prompt com o texto deste documento e as regras da KAIRÓS. ' +
                        'A conferência é a mais dura do Hub: todo valor, CPF, CNPJ e data que a IA ' +
                        'escrever precisa aparecer no arquivo.',
                      rotuloGravar: 'Gravar como campo desta seção',
                      gerar: async () => {
                        const r = await apiDocumentos.promptDeResumo(d.id);
                        /* ⚠️ **O número importa.** "Foi cortado" não diz se
                           faltou um parágrafo ou metade do instrumento — e é a
                           diferença entre confiar no resumo e reler o arquivo. */
                        if (r.cortou) {
                          avisar(
                            `Documento longo: entraram ${r.entrou}% dele — o começo e o fim. ` +
                              'O miolo ficou de fora, e o prompt manda dizer isso no resumo.',
                          );
                        }
                        return r.prompt;
                      },
                      conferir: async (t) => (await apiDocumentos.conferirResumo(d.id, t)).achados,
                    }}
                    aoAceitar={async (texto) => {
                      /*
                       * ⚠️ **Vira CAMPO LIVRE, e não o campo fixo da seção.** O
                       * campo fixo é escrito pela análise societária; sobrescrevê-lo
                       * com um resumo apagaria a linha do tempo que ela montou.
                       * ⭐ E o título leva o nome do arquivo: quem lê o relatório
                       * precisa saber de qual documento o resumo saiu.
                       */
                      const nova = await apiRelatorios.criarAnotacao(
                        relatorioId,
                        secaoId,
                        `Resumo — ${d.nome.replace(/\.pdf$/i, '')}`,
                      );
                      await apiRelatorios.escreverAnotacao(relatorioId, nova.id, {
                        titulo: `Resumo — ${d.nome.replace(/\.pdf$/i, '')}`,
                        corpo: texto
                          .split(/\n\s*\n/)
                          .map((p) => p.trim())
                          .filter(Boolean)
                          .map((p) => ({ especie: 'paragrafo' as const, trechos: [{ t: p }] })),
                      });
                      setAResumir(null);
                      await aoGravarSecao();
                      avisar('Resumo gravado como campo desta seção.');
                    }}
                  />
                  <button type="button" className="imv-bt fantasma p" onClick={() => setAResumir(null)}>
                    Fechar
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {analise && <Resultado analise={analise} travado={travado} aoGravar={() => void analisar(true)} />}

      {aApagar && (
        <Confirmar
          titulo="Apagar este documento?"
          descricao={`"${aApagar.nome}" sai do Hub e do backup. Se foi o cliente que entregou, pode não haver segunda via.`}
          rotuloAcao="Apagar"
          aoConfirmar={async () => {
            try {
              await apiDocumentos.apagar(aApagar.id);
              setAApagar(null);
              setAnalise(null);
              await carregar();
              avisar('Documento apagado.');
            } catch (e) {
              avisarErro(e);
            }
          }}
          aoFechar={() => setAApagar(null)}
        />
      )}
    </div>
  );
}

/** Um sócio na linha do tempo, com o que o Hub sabe e o que ele não sabe. */
function Socio({ s }: { s: SocioDoContrato }) {
  return (
    <li>
      <strong>{s.nome}</strong>
      {s.documento && <span className="dado"> · {formatarCpf(s.documento)}</span>}
      {s.quotas !== null && (
        <span className="dado"> · {s.quotas.toLocaleString('pt-BR')} quotas</span>
      )}
      {/*
        ⚠️ **A etiqueta diz de onde veio o reconhecimento.** "por nome" é aviso,
        não decoração: sem documento, o que se afirmou foi que dois nomes são
        iguais — e não que são a mesma pessoa.
      */}
      {s.papel === 'investigado' ? (
        <span className={`etiqueta ${s.reconhecidoPor === 'nome' ? 'etiqueta-a-conferir' : 'etiqueta-vinculo'}`}>
          {s.reconhecidoPor === 'nome' ? 'na ficha, por nome — confira' : 'na ficha'}
        </span>
      ) : (
        <span className="etiqueta">fora da ficha</span>
      )}
    </li>
  );
}

const formatarCpf = (d: string) =>
  d.length === 11 ? d.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4') : d;

function Resultado({
  analise,
  travado,
  aoGravar,
}: {
  analise: AnaliseSocietaria;
  travado: boolean;
  aoGravar: () => void;
}) {
  const emData = (d: string | null) => (d ? d.split('-').reverse().join('/') : 'sem data');

  return (
    <div className="analise-societaria">
      <p className="filtro-ativo">
        {analise.documentos.lidos} documento(s) lido(s)
        {analise.documentos.comFalha > 0 && `, ${analise.documentos.comFalha} sem texto para ler`}.{' '}
        {analise.passos.length} instrumento(s) na linha do tempo.
      </p>

      {/* ⚠️ A lacuna vem ANTES do resultado: sem ela, o que foi achado parece a
          análise inteira quando parte nem foi avaliada. */}
      {[...analise.lacunas, ...analise.lacunasDosSinais].map((l) => (
        <p key={l} className="ajuda-alerta ajuda-solta">
          {l}
        </p>
      ))}

      {/*
        ⭐ **O nome da empresa aparece quando o caso tem MAIS DE UMA — 09/09/2026.**

        ⛔ **A linha do tempo misturava empresas e ninguém via.** No caso real
        foram anexadas a ficha da empresa devedora e o registro do MEI de um dos sócios;
        os atos entraram numa cronologia só e o Richard disse, com razão, que
        *"está bem confuso"*. O servidor já parou de comparar quadros de
        empresas diferentes; aqui a tela passa a dizer de quem é cada ato.

        ⚠️ **Só quando há mais de uma.** Repetir o mesmo nome em todos os passos
        de um caso com uma empresa só é ruído que ensina o olho a pular a linha.
      */}
      {analise.passos.map((p, i) => (
        <div key={`${p.data}-${i}`} className="passo-societario">
          {new Set(analise.passos.map((x) => x.empresaChave)).size > 1 && p.empresa.nome && (
            <p className="rotulo">{p.empresa.nome}</p>
          )}
          <p className="passo-cabecalho">
            <span className="dado">{emData(p.data)}</span> · {p.titulo ?? 'Instrumento'}
          </p>
          {p.capital?.valor && (
            <p className="ajuda">
              Capital <span className="dado">R$ {Number(p.capital.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
              {p.capital.quotas !== null && ` · ${p.capital.quotas.toLocaleString('pt-BR')} quotas`}
            </p>
          )}
          {p.endereco && <p className="ajuda">Sede: {p.endereco}</p>}
          <ul className="socios-do-passo">
            {p.socios.map((s, j) => (
              <Socio key={`${s.documento ?? s.nome}-${j}`} s={s} />
            ))}
          </ul>
          {p.movimentos.map((m, j) => (
            <p key={j} className="movimento-de-quotas">
              <strong>Transferência:</strong> {m.de} → {m.para}
              {m.quotas !== null && ` · ${m.quotas.toLocaleString('pt-BR')} quotas`}
              {/* ⚠️ O trecho do contrato vem junto: afirmação sobre patrimônio de
                  terceiro sem a passagem que a sustenta não se leva a juízo. */}
              <span className="trecho-do-contrato">{m.trecho}</span>
            </p>
          ))}
          {p.mudanca.enderecoAnterior && (
            <p className="ajuda">Sede alterada. Antes: {p.mudanca.enderecoAnterior}</p>
          )}
        </div>
      ))}

      {/*
        ⛔ **O histórico de endereços também misturava empresas — 12/09/2026.**
        Os passos ganharam o nome da empresa em 09/09 e esta lista ficou para
        trás: a sede de três empresas numa sequência só parecia uma empresa
        mudando de lugar e voltando.

        ⛔ **E o nome vinha do último ato da empresa, tivesse nome ou não —
        12/09/2026.** A alteração lida de cópia costuma trazer o CNPJ sem
        repetir o nome, e a lista dizia "Empresa não identificada" onde o
        documento nomeava a empresa. Vale o último ato que TEM nome — o mesmo
        critério do cabeçalho do documento (`blocosPorEmpresa`).
      */}
      {analise.historicoDeEnderecos.length > 1 && (
        <div className="bloco-interno">
          <h4 className="rotulo">Histórico de endereços</h4>
          <ul>
            {analise.historicoDeEnderecos.map((h, i) => (
              <li key={i}>
                {new Set(analise.passos.map((x) => x.empresaChave)).size > 1 && (
                  <span className="rotulo">
                    {[...analise.passos].reverse().find((x) => x.empresaChave === h.empresaChave && x.empresa.nome)?.empresa.nome ?? 'Empresa não identificada'}
                    {' · '}
                  </span>
                )}
                <span className="dado">{emData(h.data)}</span> — {h.endereco}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/*
        ⚠️ **Este bloco é o mais delicado da tela, e o texto foi escolhido
        palavra por palavra.**

        Ele NÃO diz "laranja". Diz "sinais que merecem olhar" — a mesma redação
        do painel de interposição, pela mesma razão: "laranja" é acusação, e
        quem acusa num documento assinado precisa de OAB atrás. Decisão do
        Richard, 18/08/2026.

        ⚠️ **E fica FORA do relatório de propósito.** O que o cliente recebe é a
        conclusão que a Karen escrever, não a lista de indícios que a leitura
        levantou.
      */}
      {analise.sinais.length > 0 && (
        <div className="bloco-interno">
          <h4 className="rotulo">Sinais que merecem olhar</h4>
          <p className="ajuda ajuda-solta">
            <strong>Nenhum sinal isolado conclui nada</strong> — os tribunais exigem
            a conjugação de elementos, e quem conjuga é quem assina.{' '}
            <strong>Isto não entra no relatório.</strong>
          </p>
          <ul>
            {analise.sinais.map((s) => (
              <li key={s.chave} className={s.peso === 'forte' ? 'sinal-forte' : 'sinal-medio'}>
                {s.texto}
              </li>
            ))}
          </ul>
        </div>
      )}

      {analise.foraDaFicha.length > 0 && (
        <div className="bloco-interno">
          <h4 className="rotulo">Aparecem no contrato e não estão na ficha</h4>
          <p className="ajuda ajuda-solta">
            Cadastrar como investigado é decisão de quem apura, e consome a cota do
            nível.
          </p>
          <ul className="socios-do-passo">
            {analise.foraDaFicha.map((s, i) => (
              <Socio key={`${s.documento ?? s.nome}-${i}`} s={s} />
            ))}
          </ul>
        </div>
      )}

      <div className="bloco-interno">
        <h4 className="rotulo">O que vai para a seção</h4>
        {/* ⭐ 15/09/2026: a evolução das empresas que são parte vai à qualificação de cada uma. */}
        {(analise.naQualificacao ?? []).length > 0 && (
          <p className="ajuda ajuda-solta">
            A evolução do quadro de{' '}
            {analise.naQualificacao!.map((x) => x.nome).join(', ')} não entra aqui: vai à qualificação
            de cada uma, pelo botão “Levar à qualificação desta empresa” no tópico Qualificação das
            partes investigadas. Aqui ficam as empresas que não são parte do caso e as ressalvas.
          </p>
        )}
        <pre className="previa-da-secao">
          {analise.texto ||
            ((analise.naQualificacao ?? []).length > 0
              ? '(nenhuma empresa fora das partes do caso, e nenhuma ressalva)'
              : '(a leitura não produziu texto)')}
        </pre>
        {!travado && (
          <button
            type="button"
            className="botao-primario"
            onClick={aoGravar}
            disabled={!analise.texto}
          >
            Preencher a seção com este texto
          </button>
        )}
        {/* ⛔ Nenhum botão apaga o que o caso já tem (decisão dele, 15/09/2026):
            com a seção já escrita, o botão não grava e diz por quê. */}
        <p className="ajuda">
          Só preenche a seção quando ela está vazia — o que já está escrito nunca é substituído.
          Revise antes de entregar: o documento é assinado por gente.
        </p>
      </div>
    </div>
  );
}
